/**
 * MA-THEME-ROLLOUT-075 + MA-THEME-PRESET-REDESIGN-076: Canonical Theme Engine
 *
 * 6-preset system. Each preset defines both light and dark token variants.
 * Mirrored in backend: /app/backend/app/theme_engine.py
 */

import { SHARED_LIGHT_THEME_TOKENS as SLT } from './sharedLightThemeTokens';

export function hexToRgb(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function rgbToHex(r, g, b) {
  r = Math.max(0, Math.min(255, Math.round(r)));
  g = Math.max(0, Math.min(255, Math.round(g)));
  b = Math.max(0, Math.min(255, Math.round(b)));
  return '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
}
export function hexToHSL(hex) {
  let [r, g, b] = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  let h, s; const l = (mx + mn) / 2;
  if (mx === mn) { h = s = 0; } else {
    const d = mx - mn; s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (mx === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s * 100, l * 100];
}
function hue2rgb(p, q, t) {
  if (t < 0) t += 1; if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}
export function hslToHex(h, s, l) {
  h /= 360; s /= 100; l /= 100; let r, g, b;
  if (s === 0) { r = g = b = l; } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s; const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1 / 3);
  }
  return rgbToHex(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255));
}
export function darken(hex, pct) { const [r, g, b] = hexToRgb(hex); const f = 1 - pct / 100; return rgbToHex(Math.round(r * f), Math.round(g * f), Math.round(b * f)); }
export function lighten(hex, pct) { const [r, g, b] = hexToRgb(hex); const f = pct / 100; return rgbToHex(Math.round(r + (255 - r) * f), Math.round(g + (255 - g) * f), Math.round(b + (255 - b) * f)); }
export function setAlpha(hex, alpha) {
  const [r, g, b] = hexToRgb(hex); const a = Math.round(alpha * 100) / 100;
  if (a === Math.floor(a)) return `rgba(${r},${g},${b},${Math.floor(a)})`;
  return `rgba(${r},${g},${b},${a})`;
}
export function contrastSafe(hex) {
  const [r, g, b] = hexToRgb(hex);
  function srgb(c) { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  return 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b) > 0.4 ? '#000000' : '#ffffff';
}
export function colorDistance(h1, h2) { const [r1, g1, b1] = hexToRgb(h1); const [r2, g2, b2] = hexToRgb(h2); return Math.sqrt((r2 - r1) ** 2 + (g2 - g1) ** 2 + (b2 - b1) ** 2); }
function simpleHash(s) { let h = 5381; for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0; return h.toString(16).padStart(8, '0').slice(0, 8); }
export function normalizeAnchorColor(hex) {
  const [h, s, l] = hexToHSL(hex);
  const ns = Math.max(s, 20); const nl = Math.min(Math.max(l, 25), 65);
  let fs = ns; if (fs > 90 && nl > 50) fs = 80;
  return hslToHex(h, fs, nl);
}

const LEGACY_PRESET_MAP = {
  light_professional: 'professional', dark_premium: 'premium_dark',
  high_contrast: 'bold_contrast', growth: 'soft_neutral', warm_energy: 'soft_neutral',
};

export const PRESETS = {
  professional: {
    name: 'Professional', mode: 'light',
    tokens: {
      light: {
        // MA-305: Consume shared Light Theme constants
        surface_base: SLT.page_bg, 
        surface_elevated: SLT.surface_elevated, 
        surface_subtle: SLT.surface_subtle,  // MA-279C: Aligned with LP canonical cardBg
        surface_response: SLT.surface_response, // MA-305: Explicit response area background
        text_primary: SLT.text_primary_funnel, 
        text_secondary: SLT.text_secondary_funnel, 
        text_inverse: SLT.text_inverse,
        border_subtle: SLT.border_subtle_hex, 
        border_strong: SLT.border_strong,
      },
      dark: {
        surface_base: '#0f172a', surface_elevated: '#1e293b', surface_subtle: '#1a2234',
        surface_response: '#1a2234', // MA-305: Explicit response area background
        text_primary: 'rgba(248,250,252,0.90)', text_secondary: 'rgba(148,163,184,0.80)', text_inverse: '#ffffff',
        border_subtle: 'rgba(148,163,184,0.10)', border_strong: 'rgba(148,163,184,0.18)',
      },
    },
  },
  premium_dark: {
    name: 'Premium Dark', mode: 'dark',
    tokens: {
      dark: {
        surface_base: '#080d19', surface_elevated: '#111827', surface_subtle: '#0c1322',
        surface_response: '#0c1322', // MA-305: Explicit response area background
        text_primary: 'rgba(255,255,255,0.93)', text_secondary: 'rgba(255,255,255,0.60)', text_inverse: '#ffffff',
        border_subtle: 'rgba(255,255,255,0.06)', border_strong: 'rgba(255,255,255,0.12)',
      },
      light: {
        surface_base: '#f0f2f5', surface_elevated: '#ffffff', surface_subtle: '#e4e7ec',
        surface_response: '#f8f9fa', // MA-305: Explicit response area background
        text_primary: '#111827', text_secondary: '#4b5563', text_inverse: '#ffffff',
        border_subtle: '#d1d5db', border_strong: '#9ca3af',
      },
    },
  },
  clean_minimal: {
    name: 'Clean Minimal', mode: 'light',
    tokens: {
      light: {
        surface_base: '#ffffff', surface_elevated: '#fafafa', surface_subtle: '#f7f7f8',
        surface_response: '#fafafa', // MA-305: Explicit response area background
        text_primary: '#18181b', text_secondary: '#a1a1aa', text_inverse: '#ffffff',
        border_subtle: '#f0f0f1', border_strong: '#ebebec',
      },
      dark: {
        surface_base: '#18181b', surface_elevated: '#27272a', surface_subtle: '#1f1f22',
        surface_response: '#1f1f22', // MA-305: Explicit response area background
        text_primary: 'rgba(250,250,250,0.88)', text_secondary: 'rgba(161,161,170,0.55)', text_inverse: '#ffffff',
        border_subtle: 'rgba(255,255,255,0.04)', border_strong: 'rgba(255,255,255,0.06)',
      },
    },
  },
  bold_contrast: {
    name: 'Bold Contrast', mode: 'dark',
    tokens: {
      light: {
        surface_base: '#ffffff', surface_elevated: '#f2f3f5', surface_subtle: '#eaecf0',
        surface_response: '#f2f3f5', // MA-305: Explicit response area background
        text_primary: '#030712', text_secondary: '#374151', text_inverse: '#ffffff',
        border_subtle: '#c9cdd5', border_strong: '#71717a',
      },
      dark: {
        surface_base: '#050505', surface_elevated: '#141414', surface_subtle: '#0a0a0a',
        surface_response: '#0a0a0a', // MA-305: Explicit response area background
        text_primary: '#ffffff', text_secondary: 'rgba(255,255,255,0.80)', text_inverse: '#ffffff',
        border_subtle: 'rgba(255,255,255,0.16)', border_strong: 'rgba(255,255,255,0.25)',
      },
    },
  },
  modern_vibrant: {
    name: 'Modern Vibrant', mode: 'dark',
    tokens: {
      light: {
        surface_base: '#f5f6fe', surface_elevated: '#ffffff', surface_subtle: '#f2f1fb',
        surface_response: '#fafaff', // MA-305: Explicit response area background
        text_primary: '#1a1a2e', text_secondary: '#6c6c96', text_inverse: '#ffffff',
        border_subtle: '#dcdcf0', border_strong: '#b8b8d9',
      },
      dark: {
        surface_base: '#0f0f1a', surface_elevated: '#1c1c34', surface_subtle: '#141425',
        surface_response: '#141425', // MA-305: Explicit response area background
        text_primary: 'rgba(255,255,255,0.92)', text_secondary: 'rgba(188,188,235,0.65)', text_inverse: '#ffffff',
        border_subtle: 'rgba(180,180,255,0.10)', border_strong: 'rgba(180,180,255,0.18)',
      },
    },
  },
  soft_neutral: {
    name: 'Soft Neutral', mode: 'light',
    tokens: {
      light: {
        surface_base: '#f9f7f4', surface_elevated: '#ffffff', surface_subtle: '#f2ede7',
        surface_response: '#fbf9f7', // MA-305: Explicit response area background
        text_primary: '#292524', text_secondary: '#78716c', text_inverse: '#ffffff',
        border_subtle: '#e0dbd4', border_strong: '#c8c2b9',
      },
      dark: {
        surface_base: '#1a1614', surface_elevated: '#2a2520', surface_subtle: '#221d19',
        surface_response: '#221d19', // MA-305: Explicit response area background
        text_primary: 'rgba(245,243,240,0.90)', text_secondary: 'rgba(168,162,158,0.65)', text_inverse: '#ffffff',
        border_subtle: 'rgba(200,190,175,0.10)', border_strong: 'rgba(200,190,175,0.18)',
      },
    },
  },
};

export function validatePreset(pt) {
  const sb = pt.surface_base || '', se = pt.surface_elevated || '', ss = pt.surface_subtle || '';
  for (const v of [sb, se, ss]) { if (!v.startsWith('#')) return; }
  const de = colorDistance(sb, se), ds = colorDistance(sb, ss);
  if (de <= 8) throw new Error(`Insufficient surface depth: base->elevated distance ${de.toFixed(1)} <= 8`);
  if (ds <= 4) throw new Error(`Insufficient surface grouping: base->subtle distance ${ds.toFixed(1)} <= 4`);
}

function resolvePresetKey(key) { return PRESETS[key] ? key : (LEGACY_PRESET_MAP[key] || 'professional'); }

export function resolveTheme(input) {
  let pk = resolvePresetKey(input.preset || 'professional');
  const pd = PRESETS[pk];
  const em = input.mode || pd.mode;
  const bt = pd.tokens[em] || pd.tokens[pd.mode];
  const raw = input.anchor_color || input.brand_primary || '#2563EB';
  const primary = normalizeAnchorColor(raw);
  const isDark = em === 'dark';
  const [r, g, b] = hexToRgb(primary);
  const accentSoft = `rgba(${r},${g},${b},${isDark ? '0.20' : '0.12'})`;
  const accentHoverVal = lighten(primary, 8);
  const accentBorder = `rgba(${r},${g},${b},0.35)`;
  const primaryFg = contrastSafe(primary);
  return {
    id: `theme_${simpleHash(pk + primary + em)}`, name: pd.name, mode: em,
    brand: { primary, secondary: null },
    tokens: { ...bt, primary, primary_hover: darken(primary, 8), primary_active: darken(primary, 12),
      primary_foreground: primaryFg,
      accent_soft: accentSoft, accent_hover: accentHoverVal, accent_border: accentBorder,
      text_on_accent: primaryFg, text_on_accent_soft: bt.text_primary || '#1e293b',
      success: '#22C55E', error: '#EF4444', highlight: setAlpha(primary, 0.25) },
  };
}

function isColorDark(h) { if (!h || !h.startsWith('#')) return true; const [r, g, b] = hexToRgb(h); return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5; }
function dim(h, f) { const [r, g, b] = hexToRgb(h); return `rgba(${r},${g},${b},${f})`; }

export function legacyAppearanceToTheme(a) {
  const cc = a.containerColor || '#FFFFFF', tc = a.threadColor || '#FFFFFF', hb = a.headerBackgroundColor || '#FFFFFF';
  const bt = a.botTextColor || '#1F2937', uc = a.userColor || a.prospectColor || '#007AFF', ut = a.userTextColor || '#FFFFFF';
  const dk = isColorDark(cc), m = dk ? 'dark' : 'light';
  return { id: 'legacy', name: 'Legacy', mode: m, brand: { primary: uc, secondary: null },
    tokens: { surface_base: tc, surface_elevated: cc, surface_subtle: hb,
      text_primary: bt, text_secondary: bt ? dim(bt, 0.7) : '#6B7280', text_inverse: ut,
      primary: uc, primary_hover: darken(uc, 8), primary_active: darken(uc, 12), primary_foreground: ut,
      border_subtle: dk ? 'rgba(255,255,255,0.06)' : '#e5e7eb', border_strong: dk ? 'rgba(255,255,255,0.15)' : '#cbd5e1',
      success: '#22C55E', error: '#EF4444', highlight: setAlpha(uc, 0.25) } };
}

export const TOKEN_CSS_MAP = {
  surface_base: '--ma-surface-base', surface_elevated: '--ma-surface-elevated', surface_subtle: '--ma-surface-subtle',
  text_primary: '--ma-text-primary', text_secondary: '--ma-text-secondary', text_inverse: '--ma-text-inverse',
  primary: '--ma-primary', primary_hover: '--ma-primary-hover', primary_active: '--ma-primary-active', primary_foreground: '--ma-primary-fg',
  accent_soft: '--ma-accent-soft', accent_hover: '--ma-accent-hover', accent_border: '--ma-accent-border',
  text_on_accent: '--ma-text-on-accent', text_on_accent_soft: '--ma-text-on-accent-soft',
  border_subtle: '--ma-border-subtle', border_strong: '--ma-border-strong', success: '--ma-success', error: '--ma-error', highlight: '--ma-highlight',
};

export function themeToCssVars(theme) {
  const tk = theme.tokens || []; const lines = [];
  for (const [k, v] of Object.entries(TOKEN_CSS_MAP)) { if (tk[k] != null) lines.push(`${v}: ${tk[k]};`); }
  return lines.join('\n');
}


// ============================================================
// LP CSS Variable Bridge
// ============================================================

// Core bridge mapping — single source of truth for LP token derivation.
// Both themeToLpBridgeCss() and themeToLpBridgeTokens() consume this.
function _computeLpBridgeVars(theme) {
  const t = theme.tokens || {};
  const mode = theme.mode || 'light';
  const isDark = mode === 'dark';
  const primary = t.primary || '#6366F1';
  const [r, g, b] = hexToRgb(primary);
  const accentRgb = `${r},${g},${b}`;

  const cardShadowPrimary = isDark ? '0 8px 24px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)' : '0 8px 24px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)';
  const cardShadowSecondary = isDark ? '0 4px 12px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.04)';
  const cardInnerHighlight = isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'inset 0 1px 0 rgba(255,255,255,0.9)';
  const textMuted = isDark ? 'rgba(255,255,255,0.45)' : '#94a3b8';
  const textInverseSub = isDark ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.7)';
  const textInverseMuted = isDark ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.5)';
  const cardBorder = `1px solid ${t.border_subtle || (isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0')}`;
  const borderSubtleVal = `1px solid ${t.border_subtle || (isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0')}`;
  const success = isDark ? '#22c55e' : '#16a34a';
  const successBg = isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4';
  const successBorder = isDark ? 'rgba(34,197,94,0.15)' : '#bbf7d0';

  return {
    '--lp-bg-page': t.surface_base || '', '--lp-surface-1': t.surface_subtle || '', '--lp-surface-2': t.surface_subtle || '',
    '--lp-surface-3': t.surface_base || '', '--lp-card-bg': t.surface_elevated || '', '--lp-elevated-bg': t.surface_subtle || '',
    '--lp-card-border': cardBorder, '--lp-card-shadow-primary': cardShadowPrimary,
    '--lp-card-shadow-secondary': cardShadowSecondary,
    '--lp-card-inner-highlight': cardInnerHighlight,
    '--lp-text-primary': t.text_primary || '', '--lp-text-secondary': t.text_secondary || '', '--lp-text-muted': textMuted,
    '--lp-text-inverse': t.text_inverse || '#ffffff', '--lp-text-inverse-sub': textInverseSub, '--lp-text-inverse-muted': textInverseMuted,
    '--lp-accent': primary, '--lp-accent-strong': t.primary_hover || darken(primary, 15),
    '--lp-accent-glow': `rgba(${accentRgb},${isDark ? '0.25' : '0.15'})`,
    '--lp-accent-light': t.accent_soft || `rgba(${accentRgb},${isDark ? '0.20' : '0.12'})`,
    '--lp-accent-border': t.accent_border || `rgba(${accentRgb},0.35)`,
    '--lp-text-on-accent': t.text_on_accent || t.primary_foreground || '#ffffff',
    '--lp-text-on-accent-soft': t.text_on_accent_soft || t.text_primary || '',
    '--lp-success': success, '--lp-success-bg': successBg, '--lp-success-border': successBorder,
    '--lp-danger': primary, '--lp-danger-bg': `rgba(${accentRgb},0.08)`, '--lp-danger-border': `rgba(${accentRgb},0.15)`,
    '--lp-border-subtle': borderSubtleVal,
    '--lp-radius-lg': '16px', '--lp-radius-md': '12px', '--lp-radius-btn': '10px', '--lp-radius-pill': '100px',
    '--lp-space-xs': '8px', '--lp-space-sm': '16px', '--lp-space-md': '24px', '--lp-space-lg': '40px',
    '--lp-space-xl': '64px', '--lp-space-section': '40px', '--lp-space-card': '24px',
    '--lp-font-heading': "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
    '--lp-font-body': "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",
    '--lp-max-w': '780px', '--lp-surface-mode': mode,
  };
}

export function themeToLpBridgeCss(theme) {
  const vars = _computeLpBridgeVars(theme);
  const lines = [];
  for (const [k, v] of Object.entries(vars)) { if (v) lines.push(`        ${k}: ${v};`); }
  return lines.join('\n');
}

// CSS var name → camelCase JS key mapping (for inline style consumption)
const LP_VAR_TO_JS = {
  '--lp-bg-page': 'bgPage', '--lp-surface-1': 'surface1', '--lp-surface-2': 'surface2',
  '--lp-surface-3': 'surface3', '--lp-card-bg': 'cardBg', '--lp-elevated-bg': 'elevatedBg',
  '--lp-card-border': 'cardBorder', '--lp-card-shadow-primary': 'cardShadow',
  '--lp-card-shadow-secondary': 'cardShadowSecondary',
  '--lp-card-inner-highlight': 'cardInnerHL',
  '--lp-text-primary': 'textPrimary', '--lp-text-secondary': 'textSecondary', '--lp-text-muted': 'textMuted',
  '--lp-text-inverse': 'textInverse', '--lp-text-inverse-sub': 'textInverseSub', '--lp-text-inverse-muted': 'textInverseMuted',
  '--lp-accent': 'accent', '--lp-accent-strong': 'accentStrong',
  '--lp-accent-glow': 'accentGlow', '--lp-accent-light': 'accentLight',
  '--lp-accent-border': 'accentBorder',
  '--lp-text-on-accent': 'textOnAccent', '--lp-text-on-accent-soft': 'textOnAccentSoft',
  '--lp-success': 'success', '--lp-success-bg': 'successBg', '--lp-success-border': 'successBorder',
  '--lp-danger': 'danger', '--lp-danger-bg': 'dangerBg', '--lp-danger-border': 'dangerBorder',
  '--lp-border-subtle': 'borderSubtle',
  '--lp-radius-lg': 'radiusLg', '--lp-radius-md': 'radiusMd', '--lp-radius-btn': 'radiusBtn', '--lp-radius-pill': 'radiusPill',
  '--lp-space-xs': 'spaceXs', '--lp-space-sm': 'spaceSm', '--lp-space-md': 'spaceMd', '--lp-space-lg': 'spaceLg',
  '--lp-space-xl': 'spaceXl', '--lp-space-section': 'spaceSection', '--lp-space-card': 'spaceCard',
  '--lp-font-heading': 'fontHeading', '--lp-font-body': 'fontBody',
  '--lp-max-w': 'maxW', '--lp-surface-mode': 'surfaceMode',
};

/**
 * Thin adapter over the canonical bridge mapping.
 * Returns a JS object with camelCase keys matching LPSectionPreview's _T shape.
 * Same token values as themeToLpBridgeCss() — single source of truth.
 */
export function themeToLpBridgeTokens(theme) {
  const vars = _computeLpBridgeVars(theme);
  const tokens = {};
  for (const [cssVar, val] of Object.entries(vars)) {
    const jsKey = LP_VAR_TO_JS[cssVar];
    if (jsKey && val) tokens[jsKey] = val;
  }
  return tokens;
}

export function resolveLpTheme(versionDoc, tenantBrand) {
  // MA-304: Priority 1 — Use stored final_theme if available
  // This is the pre-computed theme from PPB Foundation Step
  if (versionDoc.final_theme) {
    return versionDoc.final_theme;
  }
  
  const stylingMode = versionDoc.styling_mode || 'legacy';
  if (stylingMode === 'inherit_brand') {
    if (tenantBrand?.default_theme) return tenantBrand.default_theme;
    return resolveTheme({ preset: 'professional' });
  }
  if (stylingMode === 'custom') {
    return resolveTheme(versionDoc.custom_theme || { preset: 'professional' });
  }
  return null;
}


// ============================================================
// Funnel Appearance Adapter
// ============================================================

/**
 * Pure mapping: canonical theme → funnel-compatible appearance object.
 * MA-279B: Corrected light/dark semantic mapping for iMessage visual model.
 * 
 * Visual hierarchy:
 * - Dark mode: surface_base (darkest) = thread, surface_elevated (lighter) = bot bubble
 * - Light mode: surface_elevated (white) = thread, surface_subtle (gray) = bot bubble
 * 
 * NO hardcoded colors — preserves theme system integrity.
 */
export function themeToFunnelAppearance(theme) {
  if (!theme?.tokens) return null;
  const t = theme.tokens;
  const isDark = theme.mode === 'dark';
  
  return {
    accent: t.primary,
    // MA-279B: Semantic token mapping (NO hardcoded values)
    containerColor: isDark ? t.surface_elevated : t.surface_subtle,        // Dark: lighter, Light: gray
    threadColor: isDark ? t.surface_base : t.surface_elevated,             // Dark: darkest, Light: white
    headerBackgroundColor: isDark ? t.surface_elevated : t.surface_subtle,
    botTextColor: t.text_primary,
    userColor: t.primary,
    userTextColor: t.text_on_accent,
    surfaceMode: theme.mode,
  };
}
